import React, { Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { PeriodFilter } from 'src/components/domain/metrics/period-filter';
import {
  PeriodFilterContext,
  periodFilterContextDefaultValue,
  PeriodFilterContextProps,
  PeriodFilterContextProvider,
} from 'src/components/domain/metrics/period-filter-context';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';

type AiAgentAnalyticsFiltersContextProps = PeriodFilterContextProps & {
  aiAgentId: string | null;
  setAiAgentId: Dispatch<SetStateAction<string | null>>;
};

export const AiAgentAnalyticsFiltersContext =
  React.createContext<AiAgentAnalyticsFiltersContextProps>({
    ...periodFilterContextDefaultValue,
    aiAgentId: null,
    setAiAgentId: () => {},
  });

type AiAgentAnalyticsFiltersProviderProps = {
  aiAgentId?: string;
  children: React.ReactNode;
};

const AiAgentScopeProvider = ({
  aiAgentId: fixedAiAgentId,
  children,
}: AiAgentAnalyticsFiltersProviderProps) => {
  const periodFilter = useContext(PeriodFilterContext);
  const [aiAgentId, setAiAgentId] = useState<string | null>(fixedAiAgentId ?? null);

  const value = useMemo(
    () => ({ ...periodFilter, aiAgentId, setAiAgentId }),
    [periodFilter, aiAgentId],
  );

  return (
    <AiAgentAnalyticsFiltersContext.Provider value={value}>
      {children}
    </AiAgentAnalyticsFiltersContext.Provider>
  );
};

export const AiAgentAnalyticsFiltersProvider = ({
  aiAgentId,
  children,
}: AiAgentAnalyticsFiltersProviderProps) => (
  <PeriodFilterContextProvider>
    <AiAgentScopeProvider aiAgentId={aiAgentId}>{children}</AiAgentScopeProvider>
  </PeriodFilterContextProvider>
);

const FiltersContainer = styled(FlexRow)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 20px;

  ${up('md')} {
    justify-content: space-between;
    margin-bottom: 30px;
    padding: 0 0 30px;
  }
`;

type AiAgentAnalyticsFiltersProps = {
  children?: React.ReactNode;
  className?: string;
};

export const AiAgentAnalyticsFilters = ({
  children,
  className,
}: AiAgentAnalyticsFiltersProps) => {
  const breakpointUpMd = useBreakpoint(up('md'));

  return (
    <FiltersContainer className={className} data-lgg-id="ai-agent-analytics-filters">
      {breakpointUpMd ? (
        <FlexRow>
          <PeriodFilter />
        </FlexRow>
      ) : (
        <PeriodFilter />
      )}
      {children}
    </FiltersContainer>
  );
};
